import { API_LABEL, type ApiKind, type RegionKey } from "./model";
import { formatCompactCurrency } from "./metricsFormat";
import type {
  ApiActivitySignals,
  KpiEmphasis,
  MetricsKpiEmphasis,
  MetricsKpis,
  PartnerMetricsRow,
} from "./metrics.types";

export type MetricsInsightScope =
  | { kind: "portfolio"; rows: readonly PartnerMetricsRow[] }
  | { kind: "partner"; row: PartnerMetricsRow };

export interface MetricsInsightTile {
  key: string;
  label: string;
  value: string | number;
  tooltip: string;
  emphasis: KpiEmphasis;
}

export interface MetricsInsightGroup {
  key: "contract-health" | "search-engagement";
  title: "Contract & Health" | "Search & Engagement";
  tiles: readonly MetricsInsightTile[];
}

export interface MetricsInsightProjection {
  groups: readonly MetricsInsightGroup[];
}

function computeApiSignals(
  rows: readonly PartnerMetricsRow[],
  apiType: ApiKind
): ApiActivitySignals {
  const entries = rows.flatMap((row) =>
    row.partner.usage.recentActivity
      .filter((entry) => entry.type === apiType)
      .map((entry) => ({ ...entry, partnerName: row.partner.name }))
  );

  const resultCounts = entries
    .filter((entry) => entry.status === "Hit")
    .map((entry) => entry.resultCount)
    .filter((count): count is number => count !== null);
  const avgResults = resultCounts.length
    ? Math.round(resultCounts.reduce((a, b) => a + b, 0) / resultCounts.length)
    : null;

  const regionCounts = new Map<RegionKey, number>();
  for (const entry of entries) {
    regionCounts.set(entry.region, (regionCounts.get(entry.region) ?? 0) + 1);
  }
  let topRegion: RegionKey | "—" = "—";
  let topCount = 0;
  for (const [region, count] of regionCounts) {
    if (count > topCount) {
      topRegion = region;
      topCount = count;
    }
  }

  const sampleEntry = entries.find((entry) => entry.status === "Hit" && entry.query !== "—");
  return {
    avgResults,
    topRegion,
    sample: sampleEntry?.query ?? "—",
    sampleFrom: sampleEntry?.partnerName ?? null,
  };
}

function deriveKpis(rows: readonly PartnerMetricsRow[]): MetricsKpis {
  const sum = (read: (row: PartnerMetricsRow) => number) =>
    rows.reduce((total, row) => total + read(row), 0);

  return {
    partnersShown: rows.length,
    contractOverage: sum((row) => row.overage.total),
    atOrOverCap: rows.filter(
      (row) => row.status === "Contract Breach" || row.status === "Needs Upgrade Now"
    ).length,
    upsellReady: rows.filter((row) => row.status === "Upsell Ready").length,
    avgUtilization: rows.length
      ? Math.round(sum((row) => row.utilization.maxPct) / rows.length)
      : 0,
    manualEntitlementChanges: sum((row) => row.driftCount),
    portfolioAcvValue: sum((row) => row.partner.acv),
    savedUsed: sum((row) => row.savedUsage.used),
    savedCap: sum((row) => row.savedUsage.cap),
    adhocUsed: sum((row) => row.adhocUsage.used),
    adhocCap: sum((row) => row.adhocUsage.cap),
    videoPlays30d: sum((row) => row.mediaPlayerViews30d),
    savedSignals: computeApiSignals(rows, "saved"),
    adhocSignals: computeApiSignals(rows, "adhoc"),
  };
}

function deriveEmphasis(kpis: MetricsKpis): MetricsKpiEmphasis {
  return {
    contractOverage: kpis.contractOverage > 0 ? "critical" : "none",
    atOrOverCap: kpis.atOrOverCap > 0 ? "critical" : "none",
    upsellReady: kpis.upsellReady > 0 ? "watch" : "none",
    savedUsage: kpis.savedUsed > kpis.savedCap ? "critical" : "none",
    adhocUsage: kpis.adhocUsed > kpis.adhocCap ? "critical" : "none",
  };
}

function signalsTooltip(apiLabel: string, signals: ApiActivitySignals): string {
  if (signals.sample === "—") {
    return `No recent ${apiLabel} activity logged for the partners shown.`;
  }
  const from = signals.sampleFrom ? ` — example from ${signals.sampleFrom}` : "";
  return (
    `Most frequent region among logged ${apiLabel} activity for the partners shown. ` +
    `Example query: "${signals.sample}"${from}. Computed from the stub Recent Activity ` +
    `log (a handful of rows per partner), not a full 30-day export.`
  );
}

function signalTiles(apiType: ApiKind, signals: ApiActivitySignals): readonly MetricsInsightTile[] {
  const label = API_LABEL[apiType];
  const tooltipLabel = apiType === "saved" ? "saved-search" : "ad hoc";
  return [
    {
      key: `${apiType}results`,
      label: `Avg. Results / ${label}`,
      value: signals.avgResults === null ? "—" : signals.avgResults.toLocaleString(),
      tooltip: `Average result count from ${tooltipLabel} activity for the partners shown. ${signalsTooltip(tooltipLabel, signals)}`,
      emphasis: "none",
    },
    {
      key: `${apiType}region`,
      label: `${label} — Top Region / Sample Query`,
      value: signals.topRegion,
      tooltip: signalsTooltip(tooltipLabel, signals),
      emphasis: "none",
    },
  ];
}

function contractAndHealth(kpis: MetricsKpis, emphasis: MetricsKpiEmphasis): MetricsInsightGroup {
  return {
    key: "contract-health",
    title: "Contract & Health",
    tiles: [
      {
        key: "partners",
        label: "Partners Shown",
        value: kpis.partnersShown,
        tooltip: "Partners matching the Partner selector and filters above.",
        emphasis: "none",
      },
      {
        key: "overage",
        label: "Contract Overage (30d)",
        value: kpis.contractOverage.toLocaleString(),
        tooltip: "Calls beyond any contracted cap, summed across shown partners.",
        emphasis: emphasis.contractOverage,
      },
      {
        key: "atcap",
        label: "At / Over Cap",
        value: kpis.atOrOverCap,
        tooltip: "Partners at or over their package cap (Contract Breach + Needs Upgrade Now).",
        emphasis: emphasis.atOrOverCap,
      },
      {
        key: "upsell",
        label: "Upsell Ready",
        value: kpis.upsellReady,
        tooltip: "Utilization ≥ 75% of cap and trending up — a natural upsell conversation.",
        emphasis: emphasis.upsellReady,
      },
      {
        key: "util",
        label: "Avg. Utilization",
        value: `${kpis.avgUtilization}%`,
        tooltip: "Average of each partner's higher utilization (Saved or Ad Hoc), as a % of cap.",
        emphasis: "none",
      },
      {
        key: "drift",
        label: "Manual Entitlement Changes",
        value: kpis.manualEntitlementChanges,
        tooltip: "Cap adjustments made directly on an account, outside a standard tier change.",
        emphasis: "none",
      },
      {
        key: "acv",
        label: "Portfolio ACV",
        value: formatCompactCurrency(kpis.portfolioAcvValue),
        tooltip: "Sum of annual contract value across partners shown.",
        emphasis: "none",
      },
    ],
  };
}

function searchAndEngagement(kpis: MetricsKpis, emphasis: MetricsKpiEmphasis): MetricsInsightGroup {
  return {
    key: "search-engagement",
    title: "Search & Engagement",
    tiles: [
      {
        key: "savedvol",
        label: "Saved Search Volume",
        value: `${kpis.savedUsed.toLocaleString()} / ${kpis.savedCap.toLocaleString()}`,
        tooltip: "Saved-search calls used vs the summed package caps of shown partners.",
        emphasis: emphasis.savedUsage,
      },
      ...signalTiles("saved", kpis.savedSignals),
      {
        key: "adhocvol",
        label: "Ad Hoc Volume",
        value: `${kpis.adhocUsed.toLocaleString()} / ${kpis.adhocCap.toLocaleString()}`,
        tooltip: "Ad-hoc calls used vs the summed package caps of shown partners.",
        emphasis: emphasis.adhocUsage,
      },
      ...signalTiles("adhoc", kpis.adhocSignals),
      {
        key: "video",
        label: "Video Plays (30d)",
        value: kpis.videoPlays30d.toLocaleString(),
        tooltip: "Media-player views across shown partners in the last 30 days.",
        emphasis: "none",
      },
    ],
  };
}

/**
 * Projects a filtered portfolio or one Partner into display-ready Metrics
 * insight groups. Calculation, emphasis, copy, formatting, and group order are
 * implementation details behind this seam.
 */
export function projectMetricsInsight(scope: MetricsInsightScope): MetricsInsightProjection {
  const rows = scope.kind === "portfolio" ? scope.rows : [scope.row];
  const kpis = deriveKpis(rows);
  const emphasis = deriveEmphasis(kpis);
  const search = searchAndEngagement(kpis, emphasis);

  return {
    groups: scope.kind === "portfolio" ? [contractAndHealth(kpis, emphasis), search] : [search],
  };
}
